/**
 * AlertsPanel - Display items requiring attention
 *
 * Features:
 * - Grouped alerts by type with count badges
 * - Icon and severity color (red=high, amber=medium, slate=low)
 * - Click to navigate to relevant section
 * - Empty state: "No issues requiring attention"
 * - Hover effects on clickable items
 */
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { cn } from '@respectly/ui';
import {
  AlertCircle,
  AlertTriangle,
  Flag,
  Activity,
  CheckCircle2,
  ChevronRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';

// ============================================
// Types
// ============================================

export interface Alert {
  id: string;
  type: 'past-due' | 'ai-flags' | 'unhealthy-service' | 'failed-jobs';
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  count: number;
  navigateTo?: string;
}

interface AlertsPanelProps {
  alerts: Alert[];
  isLoading: boolean;
}

// Alert configuration
const ALERT_CONFIG = {
  'past-due': {
    icon: AlertCircle,
    color: 'red',
  },
  'ai-flags': {
    icon: Flag,
    color: 'amber',
  },
  'unhealthy-service': {
    icon: AlertTriangle,
    color: 'red',
  },
  'failed-jobs': {
    icon: Activity,
    color: 'amber',
  },
};

// Severity colors
const SEVERITY_COLORS = {
  high: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  medium:
    'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  low: 'text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700',
};

// ============================================
// Component
// ============================================

export function AlertsPanel({ alerts, isLoading }: AlertsPanelProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return <AlertsPanelSkeleton />;
  }

  const handleAlertClick = (alert: Alert) => {
    if (!alert.navigateTo) return;

    navigate({ to: alert.navigateTo as any });
  };

  if (alerts.length === 0) {
    return (
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-slate-400" />
            Attention Required
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-center py-8">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500 opacity-60" />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No issues requiring attention
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Attention Required
          <span className="ml-auto text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
            {alerts.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {alerts.map((alert, index) => {
            const config = ALERT_CONFIG[alert.type];
            const Icon = config.icon;
            const severityColor = SEVERITY_COLORS[alert.severity];
            const isClickable = !!alert.navigateTo;

            return (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => isClickable && handleAlertClick(alert)}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-lg border',
                  severityColor,
                  isClickable &&
                    'cursor-pointer hover:shadow-sm transition-all duration-200'
                )}
              >
                {/* Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  <Icon className="h-4 w-4" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold">{alert.title}</p>
                    <span className="flex-shrink-0 text-xs font-bold tabular-nums px-2 py-0.5 rounded-full bg-white/50 dark:bg-black/20">
                      {alert.count}
                    </span>
                  </div>
                  <p className="text-xs mt-0.5 opacity-90">{alert.description}</p>
                </div>

                {/* Arrow */}
                {isClickable && (
                  <div className="flex-shrink-0">
                    <ChevronRight className="h-4 w-4 opacity-50" />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function AlertsPanelSkeleton() {
  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="h-6 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          <div className="ml-auto h-5 w-8 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 animate-pulse"
            >
              <div className="h-4 w-4 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-3 w-full bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
              <div className="h-4 w-8 bg-slate-200 dark:bg-slate-700 rounded-full" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
